import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import * as companyService from '../services/companyService';
import { Company, UpdateCompanyInput } from '../services/companyService';

/**
 * Company Settings Page
 *
 * Allows Company Admins to manage their company profile including:
 * - Basic information (name, website, industry)
 * - Contact details (phone, billing email)
 * - Address information
 * - Company details (size, industry)
 */

export function CompanySettings(): React.ReactElement {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [company, setCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<UpdateCompanyInput>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingAbout, setIsGeneratingAbout] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirect if not Company Admin or Platform Admin
  useEffect(() => {
    if (user && user.role !== 'company_admin' && user.role !== 'platform_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load company data
  useEffect(() => {
    const loadCompany = async () => {
      if (!token || !user) return;

      setIsLoading(true);
      try {
        const companyData = await companyService.getCompanyById(user.company_id, token);
        setCompany(companyData);

        // Initialize form data
        setFormData({
          name: companyData.name,
          subdomain: companyData.subdomain,
          website: companyData.website,
          about: companyData.about,
          industry: companyData.industry,
          company_size: companyData.company_size,
          phone: companyData.phone,
          billing_email: companyData.billing_email,
          primary_contact_name: companyData.primary_contact_name,
          primary_contact_phone: companyData.primary_contact_phone,
          primary_contact_email: companyData.primary_contact_email,
          secondary_contact_name: companyData.secondary_contact_name,
          secondary_contact_phone: companyData.secondary_contact_phone,
          secondary_contact_email: companyData.secondary_contact_email,
          billing_contact_name: companyData.billing_contact_name,
          billing_contact_phone: companyData.billing_contact_phone,
          billing_contact_email: companyData.billing_contact_email,
          address_line1: companyData.address_line1,
          address_line2: companyData.address_line2,
          city: companyData.city,
          state: companyData.state,
          postal_code: companyData.postal_code,
          country: companyData.country
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load company data');
      } finally {
        setIsLoading(false);
      }
    };

    loadCompany();
  }, [token, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value || null
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token || !company) return;

    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const updatedCompany = await companyService.updateCompany(company.id, formData, token);
      setCompany(updatedCompany);
      setSuccess('Company profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update company profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      // Reset form to original company data
      setFormData({
        name: company.name,
        subdomain: company.subdomain,
        website: company.website,
        about: company.about,
        industry: company.industry,
        company_size: company.company_size,
        phone: company.phone,
        billing_email: company.billing_email,
        primary_contact_name: company.primary_contact_name,
        primary_contact_phone: company.primary_contact_phone,
        primary_contact_email: company.primary_contact_email,
        secondary_contact_name: company.secondary_contact_name,
        secondary_contact_phone: company.secondary_contact_phone,
        secondary_contact_email: company.secondary_contact_email,
        billing_contact_name: company.billing_contact_name,
        billing_contact_phone: company.billing_contact_phone,
        billing_contact_email: company.billing_contact_email,
        address_line1: company.address_line1,
        address_line2: company.address_line2,
        city: company.city,
        state: company.state,
        postal_code: company.postal_code,
        country: company.country
      });
      setError('');
      setSuccess('');
    }
  };

  const handleGenerateAbout = async () => {
    if (!token || !company || !formData.website) return;

    setIsGeneratingAbout(true);
    setError('');

    try {
      // Normalize the website URL - add https:// if missing
      let websiteUrl = formData.website.trim();
      if (!websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = `https://${websiteUrl}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/companies/${company.id}/generate-about`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ website: websiteUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate about section');
      }

      const data = await response.json();

      // Update form data with generated about text
      setFormData(prev => ({
        ...prev,
        about: data.data.about
      }));

      setSuccess('About section generated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      // Check if this is an OpenAI content filtering error
      const errorMessage = err.message || 'Failed to generate about section';
      if (errorMessage.includes('content filtering') || errorMessage.includes('blocked')) {
        setError('AI generation was blocked by content filters. This is a false positive - your website content is fine. Please try again or write the About section manually.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsGeneratingAbout(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-full py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading company settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!company) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-full py-12">
          <div className="text-center">
            <p className="text-red-600">Failed to load company data</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-8 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Company Settings</h1>
              <p className="mt-2 text-blue-100">
                Manage your company profile and contact information
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-indigo-600 dark:text-white bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="company-settings-form"
                disabled={isSaving}
                className="px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-indigo-600 dark:text-white bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-600 inline"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 rounded-md bg-green-50 p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form id="company-settings-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company Name *
                  {user?.role === 'company_admin' && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Platform Admin only)</span>
                  )}
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  disabled={user?.role === 'company_admin'}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="about" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    About
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateAbout}
                    disabled={!formData.website || isGeneratingAbout}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isGeneratingAbout ? (
                      <>
                        <svg
                          className="animate-spin h-3 w-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Generate via AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  name="about"
                  id="about"
                  rows={4}
                  value={formData.about || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Tell us about your company..."
                />
                {!formData.website && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Add a website URL above to enable AI generation
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Subdomain
                  {user?.role === 'company_admin' && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Platform Admin only)</span>
                  )}
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="subdomain"
                    id="subdomain"
                    value={formData.subdomain || ''}
                    onChange={handleInputChange}
                    disabled={user?.role === 'company_admin'}
                    className="flex-1 block w-full border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 dark:text-gray-400 disabled:cursor-not-allowed"
                    placeholder="company-name"
                  />
                  <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 sm:text-sm">
                    .nivoon.com
                  </span>
                </div>
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Website
                </label>
                <input
                  type="text"
                  name="website"
                  id="website"
                  value={formData.website || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  placeholder="www.example.com or https://www.example.com"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  You can enter just the domain (e.g., www.example.com) and https:// will be added automatically
                </p>
              </div>

              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Industry
                </label>
                <select
                  name="industry"
                  id="industry"
                  value={formData.industry || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Select an industry</option>
                  <option value="Aerospace & Defense">Aerospace & Defense</option>
                  <option value="Agriculture & Food Services">Agriculture & Food Services</option>
                  <option value="Automotive">Automotive</option>
                  <option value="Construction & Real Estate">Construction & Real Estate</option>
                  <option value="Education & Training">Education & Training</option>
                  <option value="Energy & Utilities">Energy & Utilities</option>
                  <option value="Financial Services & Banking">Financial Services & Banking</option>
                  <option value="Government & Public Administration">Government & Public Administration</option>
                  <option value="Healthcare & Pharmaceuticals">Healthcare & Pharmaceuticals</option>
                  <option value="Hospitality & Tourism">Hospitality & Tourism</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Legal Services">Legal Services</option>
                  <option value="Manufacturing & Industrial">Manufacturing & Industrial</option>
                  <option value="Media & Entertainment">Media & Entertainment</option>
                  <option value="Non-Profit & Social Services">Non-Profit & Social Services</option>
                  <option value="Professional Services & Consulting">Professional Services & Consulting</option>
                  <option value="Retail & E-commerce">Retail & E-commerce</option>
                  <option value="Technology & Software">Technology & Software</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Transportation & Logistics">Transportation & Logistics</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="company_size" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company Size
                </label>
                <select
                  name="company_size"
                  id="company_size"
                  value={formData.company_size || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Select company size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501-1000">501-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-6">Contact Information</h2>

            {/* Primary Contact */}
            <div className="mb-8">
              <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                Primary Contact
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="primary_contact_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    name="primary_contact_name"
                    id="primary_contact_name"
                    value={formData.primary_contact_name || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="primary_contact_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="primary_contact_phone"
                    id="primary_contact_phone"
                    value={formData.primary_contact_phone || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="primary_contact_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    name="primary_contact_email"
                    id="primary_contact_email"
                    value={formData.primary_contact_email || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Secondary Contact */}
            <div className="mb-8">
              <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                Secondary Contact
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="secondary_contact_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    name="secondary_contact_name"
                    id="secondary_contact_name"
                    value={formData.secondary_contact_name || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Jane Smith"
                  />
                </div>

                <div>
                  <label htmlFor="secondary_contact_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="secondary_contact_phone"
                    id="secondary_contact_phone"
                    value={formData.secondary_contact_phone || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="secondary_contact_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    name="secondary_contact_email"
                    id="secondary_contact_email"
                    value={formData.secondary_contact_email || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Billing / Accounting Contact */}
            <div>
              <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                Billing / Accounting Contact
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="billing_contact_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    name="billing_contact_name"
                    id="billing_contact_name"
                    value={formData.billing_contact_name || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Bob Johnson"
                  />
                </div>

                <div>
                  <label htmlFor="billing_contact_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="billing_contact_phone"
                    id="billing_contact_phone"
                    value={formData.billing_contact_phone || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="billing_contact_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    name="billing_contact_email"
                    id="billing_contact_email"
                    value={formData.billing_contact_email || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="billing@example.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">Address</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Address Line 1
                </label>
                <input
                  type="text"
                  name="address_line1"
                  id="address_line1"
                  value={formData.address_line1 || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  placeholder="123 Main Street"
                />
              </div>

              <div>
                <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Address Line 2
                </label>
                <input
                  type="text"
                  name="address_line2"
                  id="address_line2"
                  value={formData.address_line2 || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Suite 100"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    value={formData.city || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    State/Province
                  </label>
                  <select
                    name="state"
                    id="state"
                    value={formData.state || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Select a state</option>
                    <option value="Alabama">Alabama</option>
                    <option value="Alaska">Alaska</option>
                    <option value="Arizona">Arizona</option>
                    <option value="Arkansas">Arkansas</option>
                    <option value="California">California</option>
                    <option value="Colorado">Colorado</option>
                    <option value="Connecticut">Connecticut</option>
                    <option value="Delaware">Delaware</option>
                    <option value="Florida">Florida</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Hawaii">Hawaii</option>
                    <option value="Idaho">Idaho</option>
                    <option value="Illinois">Illinois</option>
                    <option value="Indiana">Indiana</option>
                    <option value="Iowa">Iowa</option>
                    <option value="Kansas">Kansas</option>
                    <option value="Kentucky">Kentucky</option>
                    <option value="Louisiana">Louisiana</option>
                    <option value="Maine">Maine</option>
                    <option value="Maryland">Maryland</option>
                    <option value="Massachusetts">Massachusetts</option>
                    <option value="Michigan">Michigan</option>
                    <option value="Minnesota">Minnesota</option>
                    <option value="Mississippi">Mississippi</option>
                    <option value="Missouri">Missouri</option>
                    <option value="Montana">Montana</option>
                    <option value="Nebraska">Nebraska</option>
                    <option value="Nevada">Nevada</option>
                    <option value="New Hampshire">New Hampshire</option>
                    <option value="New Jersey">New Jersey</option>
                    <option value="New Mexico">New Mexico</option>
                    <option value="New York">New York</option>
                    <option value="North Carolina">North Carolina</option>
                    <option value="North Dakota">North Dakota</option>
                    <option value="Ohio">Ohio</option>
                    <option value="Oklahoma">Oklahoma</option>
                    <option value="Oregon">Oregon</option>
                    <option value="Pennsylvania">Pennsylvania</option>
                    <option value="Rhode Island">Rhode Island</option>
                    <option value="South Carolina">South Carolina</option>
                    <option value="South Dakota">South Dakota</option>
                    <option value="Tennessee">Tennessee</option>
                    <option value="Texas">Texas</option>
                    <option value="Utah">Utah</option>
                    <option value="Vermont">Vermont</option>
                    <option value="Virginia">Virginia</option>
                    <option value="Washington">Washington</option>
                    <option value="West Virginia">West Virginia</option>
                    <option value="Wisconsin">Wisconsin</option>
                    <option value="Wyoming">Wyoming</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    id="postal_code"
                    value={formData.postal_code || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  id="country"
                  value={formData.country || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          {/* Subscription Information (Read-only for Company Admin) */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">Subscription</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Plan
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-50 capitalize">{company.subscription_tier || 'Free'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-50 capitalize">{company.subscription_status || 'Trial'}</p>
              </div>

              {company.trial_ends_at && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Trial Ends
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-50">
                    {new Date(company.trial_ends_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>

        </form>
      </div>
    </Layout>
  );
}

export default CompanySettings;
